import { database } from './config';
import { ref, get, set, runTransaction } from 'firebase/database';
import { formatActDataForElo, updateEloForRace } from './eloService';
import { getAllEloRatings, setPlayerElo } from './eloManagement';

const DEFAULT_ELO = 450; // Match new sophisticated system
const HISTORICAL_K_FACTOR = 24; // Reduced K-factor for historical recalculation

console.log('🚨 ELO HISTORICAL CALCULATOR LOADED - NOW USING HISTORY-BASED SYSTEM');

// Helper functions for historical ELO calculation
function calculateExpectedScore(playerRating, avgOthersRating) {
  return 1 / (1 + Math.pow(10, (avgOthersRating - playerRating) / 400));
}

function calculateActualScore(finish, numPlayers) {
  return (numPlayers - finish) / (numPlayers - 1);
}

function calculateNewRating(currentRating, expectedScore, actualScore, kFactor) {
  const change = Math.round(kFactor * (actualScore - expectedScore));
  return currentRating + change;
}

// Note: Individual calculation functions removed - now using sophisticated system from eloService.js

/**
 * Recalculate all Elo ratings from historical data
 * @param {Array} historyData - Array of ACT entries sorted chronologically
 * @param {Object} initialRatings - Optional initial ratings from database
 * @returns {Object} Object with player ratings and history
 */
export async function recalculateEloFromHistory(historyData, initialRatings = {}) {
  console.log('📊 Recalculating Elo from', historyData.length, 'historical ACT entries using sophisticated system');
  
  // Sort history chronologically (oldest first)
  const sortedHistory = [...historyData].sort((a, b) => {
    const dateA = new Date(a.date || a.timestamp);
    const dateB = new Date(b.date || b.timestamp);
    return dateA - dateB;
  });
  
  // Initialize player ratings and history
  const playerRatings = { ...initialRatings };
  const playerHistories = {};
  
  // Process each ACT entry
  sortedHistory.forEach((actData, actIndex) => {
    const raceResults = formatActDataForElo(actData);
    const actDate = actData.date || new Date(actData.timestamp).toISOString();
    
    console.log(`Processing ACT ${actIndex + 1}/${sortedHistory.length} from ${actDate.split('T')[0]}`);
    
    // Process each race in the ACT
    raceResults.forEach((race) => {
      const playerIds = Object.keys(race.players);
      const numPlayers = playerIds.length;
      
      if (numPlayers < 2) return;
      
      // Only process players who have ratings in the database (don't initialize new players)
      const ratedPlayerIds = playerIds.filter(playerId => playerId in playerRatings);
      
      if (ratedPlayerIds.length < 2) {
        console.log(`  Skipping race - only ${ratedPlayerIds.length} rated players out of ${playerIds.length} total`);
        return; // Skip this race
      }
      
      // Initialize history for new rated players
      ratedPlayerIds.forEach(playerId => {
        if (!(playerId in playerHistories)) {
          const isFromDatabase = playerId in initialRatings;
          playerHistories[playerId] = [{
            date: actDate,
            elo: playerRatings[playerId],
            event: isFromDatabase ? 'Initial (Database Value)' : 'Initial (Default)'
          }];
        }
      });
      
      // Calculate new ratings for this race
      const newRatings = {};
      
      ratedPlayerIds.forEach(playerId => {
        const playerRating = playerRatings[playerId];
        const finish = race.players[playerId].finish;
        
        // Calculate average rating of other rated players
        const otherRatings = ratedPlayerIds
          .filter(id => id !== playerId)
          .map(id => playerRatings[id]);
        const avgOthersRating = otherRatings.reduce((sum, r) => sum + r, 0) / otherRatings.length;
        
        // Calculate expected and actual scores (using only rated players count)
        const expectedScore = calculateExpectedScore(playerRating, avgOthersRating);
        const actualScore = calculateActualScore(finish, ratedPlayerIds.length);
        
        // Calculate new rating with reduced historical impact
        newRatings[playerId] = calculateNewRating(playerRating, expectedScore, actualScore, HISTORICAL_K_FACTOR);
      });
      
      // Update ratings and history
      Object.entries(newRatings).forEach(([playerId, newRating]) => {
        const change = newRating - playerRatings[playerId];
        playerRatings[playerId] = newRating;
        
        // Add to history
        if (!playerHistories[playerId]) {
          playerHistories[playerId] = [];
        }
        playerHistories[playerId].push({
          date: actDate,
          elo: newRating,
          change,
          cup: race.cup,
          raceNum: race.raceNum,
          finish: race.players[playerId].finish,
          event: `${race.cup} Race ${race.raceNum}`
        });
      });
    });
  });
  
  console.log('✅ Elo recalculation complete');
  return {
    ratings: playerRatings,
    histories: playerHistories
  };
}

/**
 * Get current Elo ratings from Firebase (normalized)
 * @returns {Promise<Object>} Current Elo ratings
 */
export async function getCurrentEloRatings() {
  try {
    // Use the normalized version from eloManagement
    const ratings = await getAllEloRatings();
    console.log('🎯 getCurrentEloRatings - Found ratings for players:', Object.keys(ratings));
    console.log('🎯 Sample ratings:', Object.entries(ratings).slice(0, 3));
    return ratings;
  } catch (error) {
    console.error('Error fetching current Elo ratings:', error);
    return {};
  }
}

/**
 * Initialize or update Elo ratings based on historical data
 * @param {Array} historyData - Array of ACT entries
 * @param {boolean} useFirebaseDefaults - Whether to use existing Firebase ratings as defaults
 * @returns {Promise<Object>} Updated ratings and histories
 */
export async function initializeEloFromHistory(historyData, useFirebaseDefaults = true) {
  console.log('🔄 Initializing Elo ratings from historical data using SOPHISTICATED SYSTEM');
  
  let initialRatings = {};
  
  if (useFirebaseDefaults) {
    // Fetch current ratings from Firebase as starting point
    initialRatings = await getCurrentEloRatings();
    console.log('📥 Loaded', Object.keys(initialRatings).length, 'existing ratings from Firebase');
  }
  
  console.log('🎯 Will process ONLY players with existing database ratings - unrated players will show N/A!');
  
  // Recalculate from history (only processes players with existing database ratings)
  const result = await recalculateEloFromHistory(historyData, initialRatings);
  
  console.log('✅ Final ELO processing complete. Players with ratings:', Object.keys(result.ratings).length);
  console.log('📊 Top 5 players:', Object.entries(result.ratings).sort(([,a], [,b]) => b-a).slice(0,5));
  
  // Optionally update Firebase with recalculated ratings
  // This is designed to update existing database values, not create new ones
  // DISABLED: Prevent automatic Firebase updates to avoid recalculation on every page load
  // Only update Firebase through explicit admin panel actions
  // if (Object.keys(result.ratings).length > 0) {
  //   console.log('📤 Updating Firebase with recalculated ELO values for existing players...');
  //   await updateFirebaseRatings(result.ratings);
  // }
  
  return result;
}

/**
 * Update Firebase with recalculated ratings (history-based)
 * @param {Object} ratings - New ratings to save
 * @returns {Promise<void>}
 */
export async function updateFirebaseRatings(ratings) {
  console.log('📤 Updating Firebase with recalculated ratings (history-based)');
  
  try {
    await Promise.all(
      Object.entries(ratings).map(async ([playerId, rating]) => {
        // Use history-based setPlayerElo instead of direct ELO path
        await setPlayerElo(playerId, rating);
      })
    );
    
    console.log('✅ Firebase ratings updated successfully (history-based)');
  } catch (error) {
    console.error('❌ Error updating Firebase ratings:', error);
    throw error;
  }
}

/**
 * Get Elo tier information
 */
export function getEloTier(elo) {
  if (elo >= 700) return { name: 'Master', color: '#FFD700', icon: '👑' };
  if (elo >= 600) return { name: 'Expert', color: '#C0C0C0', icon: '⭐' };
  if (elo >= 550) return { name: 'Advanced', color: '#CD7F32', icon: '🏆' };
  if (elo >= 500) return { name: 'Intermediate', color: '#4CAF50', icon: '🏁' };
  if (elo >= 450) return { name: 'Beginner', color: '#2196F3', icon: '🏎️' };
  return { name: 'Novice', color: '#9E9E9E', icon: '🏃' };
}

/**
 * Format player ID for display
 */
export function formatPlayerId(playerId) {
  // Format: "A_A" -> "Team A, Player A"
  const [team, player] = playerId.split('_');
  return `Team ${team}, Player ${player}`;
}